import React from "react";
import Card from "@albertsons/uds/molecule/Card";
import Button from "@albertsons/uds/molecule/Button";
import { CheckCircle2, File, Trash2 } from "lucide-react";
import Tooltip from "@albertsons/uds/molecule/Tooltip";

interface UploadFileCardSelfServiceProps {
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  onFileSelect: (files: File[]) => void;
  onClearClick: () => void;
  isOrgSelected: boolean;
  fileInputRef?: React.RefObject<HTMLInputElement | null>;
}

const UploadFileCardSelfService: React.FC<UploadFileCardSelfServiceProps> = ({
  isOpen,
  setOpen,
  files,
  setFiles,
  onFileSelect,
  onClearClick,
  isOrgSelected,
  fileInputRef: externalFileInputRef,
}) => {
  const internalFileInputRef = React.useRef<HTMLInputElement>(null);
  const fileInputRef = externalFileInputRef || internalFileInputRef;

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      (file) => file.type === "text/csv"
    );
    
    if (droppedFiles.length > 0) {
      setFiles(droppedFiles);
      onFileSelect(droppedFiles);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      const fileArray = Array.from(selectedFiles).filter(
        (file) => file.type === "text/csv"
      );
      setFiles(fileArray);
      onFileSelect(fileArray);
    }
    // Reset the input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onDeleteFile = (file: File) => {
    const updatedFiles = files.filter((f) => f !== file);
    setFiles(updatedFiles);
    // Reset file input to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    // If no files left, trigger clear callback to clear table data
    if (updatedFiles.length === 0) {
      onClearClick();
    }
  };

  const formatFileSize = (size: number): string => {
    if (size < 1024) return size + " B";
    if (size < 1024 * 1024) return (size / 1024).toFixed(2) + " KB";
    return (size / (1024 * 1024)).toFixed(2) + " MB";
  };

  return (
    <Card
      collapsible
      title="Upload File"
      isOpen={isOpen}
      onChange={setOpen}
      className="min-w-[600px] mb-4"
    >
      <Card.Header>
        <div className="flex items-center justify-end w-full"></div>
      </Card.Header>

      <Card.Content>
        <div>
          {/* File Upload Section */}
          <div>
            <label style={{ marginRight: "20px", fontWeight: "bold" }}>
              Upload CSV File*:
            </label>
            <input
              type="file"
              ref={fileInputRef}
              hidden
              onChange={handleFileChange}
              multiple
              accept=".csv"
            />
            <div
              className="browseAndButtonsContainer"
              style={{ display: "flex", alignItems: "flex-start", marginTop: "10px" }}
            >
              <div
                className="flex flex-col items-center justify-center h-[130px] w-[450px] border-2 rounded border-dashed border-brand-light bg-blue-307"
                onDragOver={onDragOver}
                onDragEnter={onDragEnter}
                onDrop={onDrop}
              >
                <p className="p-2.5 text-base leading-5 font-normal text-light-text">
                  Drag and drop file or{" "}
                  <button
                    onClick={() => {
                      if (!isOrgSelected) {
                        return;
                      }
                      fileInputRef?.current?.click();
                    }}
                    disabled={!isOrgSelected}
                    className={`text-brand underline ${
                      !isOrgSelected
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer"
                    }`}
                  >
                    browse
                  </button>
                </p>
                {!isOrgSelected && (
                  <p className="text-xs text-gray-500 mt-1 ml-50px">
                    Please select an organization first
                  </p>
                )}
              </div>
              <div className="buttonContainer">
                <div className="clearButton">
                  <Button
                    width={120}
                    disabled={files.length === 0}
                    onClick={onClearClick}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <p className="h-full mt-1 italic text-sm leading-5 font-normal text-light-text">
            Only CSV files are allowed
          </p>
          {files.map((file) => (
            <div
              key={file.name}
              className="h-[130px] w-[450px] box-border border border-[#C8DAEB] rounded-lg bg-white mt-2.5"
            >
              <div className="flex mt-2.5 ml-2.5">
                <div className="flex items-center justify-center w-[50px] h-[50px] bg-blue-307 rounded-full">
                  <File color="#5A697B" />
                </div>
                {file.name.length > 30 ? (
                  <Tooltip zIndex={10} anchor="right" label={file.name}>
                    <span className="mt-2.5 ml-2.5 text-[16px] font-normal whitespace-nowrap cursor-pointer">
                      {file.name.slice(0, 30) + "..."}
                    </span>
                  </Tooltip>
                ) : (
                  <span className="mt-2.5 ml-2.5 text-[16px] font-normal whitespace-nowrap">
                    {file.name}
                  </span>
                )}
              </div>
              <span className="ml-[68px] relative -top-[17px] text-[12px] text-[#5A697B]">
                {formatFileSize(file.size)}
              </span>
              <div className="flex float-right -mt-[34px]">
                <CheckCircle2 color="#1BA418" width="20px" height="20px" />
                <Trash2
                  color="#5A697B"
                  width="18px"
                  height="20px"
                  className="ml-2 mr-2 cursor-pointer"
                  onClick={() => onDeleteFile(file)}
                />
              </div>
            </div>
          ))}
        </div>
      </Card.Content>
    </Card>
  );
};

export default UploadFileCardSelfService;
